import React from 'react';
import { URLS } from "@/lib/urls";
import AdminLayout from "@/components/layout/admin/adminLayout";
import { refundApi } from "@/api/refund";
import {
    AlertCircle,
    ArrowDownLeft,
    ArrowLeft,
    ArrowUpLeft,
    CheckCircle2,
    Clock3,
    Layers3,
    Plus,
    ReceiptText,
    RefreshCcw,
    XCircle,
    Wallet,
    LayoutDashboard,
    CircleDollarSign,
} from "lucide-react";
import { Card, Skeleton, Tooltip } from "antd";
import {Link} from "react-router-dom";

function AdminRefundDashboard() {
    const { data: dashboardData, isPending: isPendingGetData } =
        refundApi.useAdminDashboard();

    const data = dashboardData?.data?.data ?? {};

    const statusConfig = {
        approved: {
            title: 'تأیید شده',
            icon: CheckCircle2,
            color: 'text-emerald-600 dark:text-emerald-400',
            bg: 'bg-emerald-50 dark:bg-emerald-500/10',
            bar: 'bg-emerald-500',
        },
        pending: {
            title: 'در انتظار بررسی',
            icon: Clock3,
            color: 'text-amber-600 dark:text-amber-400',
            bg: 'bg-amber-50 dark:bg-amber-500/10',
            bar: 'bg-amber-500',
        },
        rejected: {
            title: 'رد شده',
            icon: XCircle,
            color: 'text-rose-600 dark:text-rose-400',
            bg: 'bg-rose-50 dark:bg-rose-500/10',
            bar: 'bg-rose-500',
        },
        cancelled: {
            title: 'لغو شده',
            icon: AlertCircle,
            color: 'text-slate-600 dark:text-slate-400',
            bg: 'bg-slate-100 dark:bg-slate-500/10',
            bar: 'bg-slate-400',
        },
        payed: {
            title: 'پرداخت شده',
            icon: CircleDollarSign,
            color: 'text-sky-600 dark:text-sky-400',
            bg: 'bg-sky-50 dark:bg-sky-500/10',
            bar: 'bg-sky-500',
        },
    };

    const refundStatuses = data?.count_per_status ?? [];
    const groupStatuses = data?.count_group_per_status ?? [];

    const formatNumber = (value) =>
        Number(value ?? 0).toLocaleString('fa-IR');

    const getStatus = (status) =>
        statusConfig[status] ?? {
            title: status,
            icon: ReceiptText,
            color: 'text-gray-600 dark:text-gray-400',
            bg: 'bg-gray-100 dark:bg-gray-500/10',
            bar: 'bg-gray-400',
        };

    const getCount = (statuses, status) =>
        statuses.find((item) => item.status === status)?.count ?? 0;

    const StatCard = ({ title, value, description, icon: Icon, color, bg }) => (
        <Card
            bordered={false}
            className="h-full !rounded-2xl !bg-white !shadow-sm dark:!bg-[#171a21]"
            styles={{ body: { padding: 20 } }}
        >
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        {title}
                    </p>
                    <div className="mt-3 text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
                        {isPendingGetData ? (
                            <Skeleton.Input active size="small" />
                        ) : (
                            formatNumber(value)
                        )}
                    </div>
                    {description && (
                        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                            {description}
                        </p>
                    )}
                </div>
                <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${bg}`}>
                    <Icon size={22} className={color} />
                </div>
            </div>
        </Card>
    );

    const QuickLink = ({ href, icon: Icon, title, description, color, bg }) => (
        <Link
            to={href}
            className="group flex items-center gap-4 rounded-2xl bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md dark:bg-[#171a21]"
        >
            <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${bg}`}>
                <Icon size={22} className={color} />
            </div>
            <div className="min-w-0 flex-1">
                <p className="font-semibold text-gray-800 dark:text-gray-100">
                    {title}
                </p>
                <p className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">
                    {description}
                </p>
            </div>
            <ArrowLeft
                size={18}
                className="shrink-0 text-gray-400 transition group-hover:-translate-x-1 group-hover:text-indigo-500"
            />
        </Link>
    );

    const StatusSection = ({ title, subtitle, statuses, total, icon: Icon }) => {
        const safeTotal = Number(total ?? 0);

        return (
            <Card
                bordered={false}
                className="!rounded-2xl !bg-white !shadow-sm dark:!bg-[#171a21]"
                styles={{ body: { padding: 24 } }}
            >
                <div className="mb-6 flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-50 dark:bg-indigo-500/10">
                        <Icon size={20} className="text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <div>
                        <h2 className="font-bold text-gray-900 dark:text-gray-100">
                            {title}
                        </h2>
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            {subtitle}
                        </p>
                    </div>
                    <div className="mr-auto rounded-lg bg-gray-100 px-3 py-1.5 text-sm font-semibold text-gray-700 dark:bg-white/5 dark:text-gray-300">
                        {formatNumber(safeTotal)}
                    </div>
                </div>

                <div className="space-y-5">
                    {Object.entries(statusConfig).map(([status, config]) => {
                        const count = Number(getCount(statuses, status));
                        const percentage = safeTotal > 0
                            ? Math.min((count / safeTotal) * 100, 100)
                            : 0;
                        const StatusIcon = config.icon;

                        return (
                            <div key={status}>
                                <div className="mb-2 flex items-center justify-between gap-3">
                                    <div className="flex items-center gap-2">
                                        <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${config.bg}`}>
                                            <StatusIcon size={16} className={config.color} />
                                        </div>
                                        <span className="text-sm text-gray-700 dark:text-gray-300">
                                            {config.title}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
                                            {formatNumber(count)}
                                        </span>
                                        <span className="w-12 text-left text-xs text-gray-400">
                                            {formatNumber(Math.round(percentage))}٪
                                        </span>
                                    </div>
                                </div>
                                <div className="h-2 overflow-hidden rounded-full bg-gray-100 dark:bg-white/5">
                                    <div
                                        className={`h-full rounded-full transition-all duration-500 ${config.bar}`}
                                        style={{ width: `${percentage}%` }}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </Card>
        );
    };

    return (
        <AdminLayout
            pageTitle="داشبورد بازگشت وجه"
            backTo={URLS.admin.dashboard}
        >
            <div className="min-h-full space-y-7 pb-8 text-right" dir="rtl">
                {/* Header */}
                <div className="relative overflow-hidden rounded-2xl bg-gradient-to-l from-indigo-700 via-indigo-600 to-violet-600 p-6 text-white shadow-sm sm:p-8">
                    <div className="pointer-events-none absolute -left-12 -top-20 h-56 w-56 rounded-full bg-white/5" />
                    <div className="pointer-events-none absolute -bottom-24 left-1/3 h-48 w-48 rounded-full bg-white/5" />

                    <div className="relative flex flex-col justify-between gap-5 sm:flex-row sm:items-center">
                        <div>

                            <h1 className="text-2xl font-bold sm:text-3xl">
                                داشبورد بازگشت وجه
                            </h1>
                            <p className="mt-3 max-w-xl text-sm leading-7 text-indigo-100">
                                نمای کلی درخواست‌های بازگشت وجه، وضعیت رسیدگی و مدیریت گروه‌های بازپرداخت.
                            </p>
                        </div>

                        <div className="flex shrink-0 items-center gap-3 rounded-xl border border-white/15 bg-white/10 px-4 py-3 backdrop-blur-sm">
                            <Wallet size={24} className="text-indigo-100" />
                            <div>
                                <p className="text-xs text-indigo-100">
                                    مجموع درخواست‌ها
                                </p>
                                <p className="mt-1 text-2xl font-bold">
                                    {isPendingGetData
                                        ? '...'
                                        : formatNumber(data?.count_refunds)}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Overview cards */}
                <section>
                    <div className="mb-4 flex items-center gap-2">
                        <div className="h-5 w-1 rounded-full bg-indigo-500" />
                        <h2 className="font-bold text-gray-800 dark:text-gray-100">
                            خلاصه وضعیت
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                        <StatCard
                            title="کل درخواست‌های بازگشت وجه"
                            value={data?.count_refunds}
                            description="تمام درخواست‌های ثبت‌شده"
                            icon={ReceiptText}
                            color="text-indigo-600 dark:text-indigo-400"
                            bg="bg-indigo-50 dark:bg-indigo-500/10"
                        />
                        <StatCard
                            title="درخواست‌های تأییدشده"
                            value={getCount(refundStatuses, 'approved')}
                            description="درخواست‌های تأییدشده"
                            icon={CheckCircle2}
                            color="text-emerald-600 dark:text-emerald-400"
                            bg="bg-emerald-50 dark:bg-emerald-500/10"
                        />
                        <StatCard
                            title="در انتظار بررسی"
                            value={getCount(refundStatuses, 'pending')}
                            description="نیازمند بررسی و تعیین تکلیف"
                            icon={Clock3}
                            color="text-amber-600 dark:text-amber-400"
                            bg="bg-amber-50 dark:bg-amber-500/10"
                        />
                        <StatCard
                            title="درخواست‌های پرداخت‌شده"
                            value={getCount(refundStatuses, 'payed')}
                            description="دارای وضعیت پرداخت‌شده"
                            icon={CircleDollarSign}
                            color="text-sky-600 dark:text-sky-400"
                            bg="bg-sky-50 dark:bg-sky-500/10"
                        />
                    </div>
                </section>

                {/* Quick access */}
                <section>
                    <div className="mb-4 flex items-center gap-2">
                        <div className="h-5 w-1 rounded-full bg-indigo-500" />
                        <h2 className="font-bold text-gray-800 dark:text-gray-100">
                            دسترسی سریع
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                        <QuickLink
                            href={URLS.admin.refund.group.create}
                            icon={Plus}
                            title="ثبت گروه بازگشت وجه"
                            description="ایجاد و مدیریت یک گروه جدید"
                            color="text-indigo-600 dark:text-indigo-400"
                            bg="bg-indigo-50 dark:bg-indigo-500/10"
                        />

                        <QuickLink
                            href={URLS.admin.refund.group.list}
                            icon={Layers3}
                            title="لیست گروه‌های بازگشت وجه"
                            description="مشاهده و مدیریت گروه‌های ثبت‌شده"
                            color="text-violet-600 dark:text-violet-400"
                            bg="bg-violet-50 dark:bg-violet-500/10"
                        />
                        <QuickLink
                            href={URLS.admin.refund.items}
                            icon={ReceiptText}
                            title="لیست بازگشت وجه‌ها"
                            description="مشاهده و پیگیری درخواست‌ها"
                            color="text-emerald-600 dark:text-emerald-400"
                            bg="bg-emerald-50 dark:bg-emerald-500/10"
                        />
                    </div>
                </section>

                {/* Refund statuses */}
                <section>
                    <div className="mb-4 flex items-center gap-2">
                        <div className="h-5 w-1 rounded-full bg-indigo-500" />
                        <h2 className="font-bold text-gray-800 dark:text-gray-100">
                            تحلیل وضعیت‌ها
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
                        <StatusSection
                            title="وضعیت بازگشت وجه‌ها"
                            subtitle="توزیع درخواست‌ها بر اساس وضعیت"
                            statuses={refundStatuses}
                            total={data?.count_refunds}
                            icon={ArrowDownLeft}
                        />
                        <StatusSection
                            title="وضعیت گروه‌های بازگشت وجه"
                            subtitle="توزیع گروه‌ها بر اساس وضعیت"
                            statuses={groupStatuses}
                            total={data?.count_group}
                            icon={Layers3}
                        />
                    </div>
                </section>

                {/* Group summary */}
                <section>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <StatCard
                            title="بازگشت وجه‌های بدون گروه"
                            value={data?.count_without_group}
                            description="درخواست‌هایی که به گروهی متصل نیستند"
                            icon={ReceiptText}
                            color="text-orange-600 dark:text-orange-400"
                            bg="bg-orange-50 dark:bg-orange-500/10"
                        />
                        <StatCard
                            title="کل گروه‌های بازگشت وجه"
                            value={data?.count_group}
                            description="مجموع گروه‌های ثبت‌شده"
                            icon={Layers3}
                            color="text-violet-600 dark:text-violet-400"
                            bg="bg-violet-50 dark:bg-violet-500/10"
                        />
                    </div>
                </section>
            </div>
        </AdminLayout>
    );
}

export default AdminRefundDashboard;